import { Box, Divider, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import BlockProps from '../../../models/BlockProps';
import Utils from '../../../utils/Utils';
import CloseButton from '../../Buttons/Close';
import EditableText from '../../Form/EditableText';
import BlockLinedTitleData from './BlockLinedTitleData';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import useGetBackground from '../../../hooks/UseGetBackground';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';
import useParallaxScript from '../../../hooks/useParallaxScript';
import config from '../../../config';
import BlockComponentHeadingColor from '../../../models/BlockComponentValues/BlockComponentHeadingColor';
import ModalComponent from '../../ModalComponent';
import ColorPicker from '../../Pickers/ColorPicker/ColorPicker';

function BlockLinedTitle({
  data,
  isEditing,
  isTemplate,
  editHandler,
  showImageModal,
  onCloseImageModal,
  showLineOptionsModal,
  onCloseLineOptionsModal,
}: BlockProps & { showLineOptionsModal?: boolean, onCloseLineOptionsModal?: () => void }) {
  const [props, setProps] = useState<BlockLinedTitleData>(data.data as BlockLinedTitleData);

  const [initialColorConfiguration, setInitialColorConfig] = useState<BlockComponentHeadingColor>(props.headingLines || BlockComponentHeadingColor.make());
  const [colorConfiguration, setColorConfiguration] = useState<BlockComponentHeadingColor>(props.headingLines || BlockComponentHeadingColor.make());

  useEffect(() => {
    setProps(data.data as BlockLinedTitleData);
  }, [data.data]);

  const handleUpdate = (text: string) => {
    props.title.value = text;
    editHandler(data.id, props);
  };

  const elementUuid = uuid();
  useParallaxScript(props.url?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  useEffect(() => {
    setInitialColorConfig(props.headingLines || BlockComponentHeadingColor.make());
  }, [props.headingLines]);

  // PDEV3-34【2】: reset line colors from the committed config whenever the
  // options modal (re)opens, so edits cancelled via Close don't carry over.
  useEffect(() => {
    if (showLineOptionsModal) {
      setColorConfiguration(props.headingLines || BlockComponentHeadingColor.make());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLineOptionsModal]);

  return (
    <Grid
      id={elementUuid}
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: 120,
        p: isEditing || isTemplate ? 2 : 0,
        overflow: 'hidden',
        ...useGetBackground(
          props.url?.solidColor,
          props.url?.value,
          props.url?.settings?.scale,
          props.url?.settings?.scaleBool,
        ),
        backgroundPosition: 'center 0px',
      }}
    >
      {(isEditing && !isTemplate) && (
        <BackGroundImageModal
          backgroundImage={props.url}
          isShowModal={showImageModal}
          onCloseModal={onCloseImageModal}
          handleOnConfirm={(url: BlockComponentImage) => {
            const tempBg = { ...props, url: { ...url } };
            setProps(tempBg);
            editHandler(data.id, tempBg);
          }}
        />
      )}
      <Box
        className="block-content-wrapper"
        sx={{
          borderTop: `2px solid ${initialColorConfiguration.outerLines.top}`,
          borderBottom: `2px solid ${initialColorConfiguration.outerLines.bottom}`,
          width: '100%',
          py: 1,
        }}
      >
        <Box
          sx={{
            borderTop: `1px solid ${initialColorConfiguration.innerLines.top}`,
            borderBottom: `1px solid ${initialColorConfiguration.innerLines.bottom}`,
            width: '100%',
            py: isEditing || isTemplate ? 1 : 0,
          }}
        >
          {props.title && (
            <Grid item sx={[Utils.hoverBorder(isEditing), { width: '100%' }]}>
              <CloseButton
                show={isEditing}
                updateHandler={() => {
                  props.title = undefined;
                  editHandler(data.id, props);
                }}
              />
              <EditableText
                text={props.title.value}
                textColor={props.title.settings?.textColor}
                isEditable={isEditing && !isTemplate}
                updateHandler={handleUpdate}
              />
            </Grid>
          )}
        </Box>
      </Box>

      <ModalComponent
        show={showLineOptionsModal}
        closeModal={onCloseLineOptionsModal}
        title="Border Settings"
        confirmButtonText="Apply"
        onConfirm={() => {
          const temp = { ...props, headingLines: colorConfiguration };
          setProps(temp);
          editHandler(data.id, temp);
          onCloseLineOptionsModal();
        }}
        centered
      >
        <h4 className="text-center">
          Outer Border
        </h4>

        <Divider sx={{ marginY: '12px' }} />

        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          padding: 2,
        }}
        >
          Top:
          <Box>
            <ColorPicker
              value={colorConfiguration.outerLines.top}
              onPickColor={(color) => {
                colorConfiguration.outerLines.top = color;
                setColorConfiguration({ ...colorConfiguration });
              }}
            />
          </Box>
        </Box>

        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          padding: 2,
        }}
        >
          Bottom:
          <Box>
            <ColorPicker
              value={colorConfiguration.outerLines.bottom}
              onPickColor={(color) => {
                colorConfiguration.outerLines.bottom = color;
                setColorConfiguration({ ...colorConfiguration });
              }}
            />
          </Box>
        </Box>

        <h4 className="text-center">
          Inner Border
        </h4>

        <Divider sx={{ marginY: '12px' }} />

        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          padding: 2,
        }}
        >
          Top:
          <Box>
            <ColorPicker
              value={colorConfiguration.innerLines.top}
              onPickColor={(color) => {
                colorConfiguration.innerLines.top = color;
                setColorConfiguration({ ...colorConfiguration });
              }}
            />
          </Box>
        </Box>

        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          padding: 2,
        }}
        >
          Bottom:
          <Box>
            <ColorPicker
              value={colorConfiguration.innerLines.bottom}
              onPickColor={(color) => {
                colorConfiguration.innerLines.bottom = color;
                setColorConfiguration({ ...colorConfiguration });
              }}
            />
          </Box>
        </Box>
      </ModalComponent>
    </Grid>
  );
}

export default BlockLinedTitle;
